import { RefObject, useLayoutEffect } from 'react';

const SUB_PIXEL_ROUNDING_GUARD = 1;

/**
 * Measures the rendered width of the longest line inside an element and shrinks
 * the element to that width, eliminating the trailing whitespace that appears
 * when text wraps inside a constrained container.
 */
const useShrinkToFitWidth = (ref: RefObject<HTMLElement>): void => {
  useLayoutEffect(() => {
    // Runs once on mount. Re-mount the component to recalculate width.
    const el = ref.current;
    if (!el) return;

    el.style.width = '';

    const range = document.createRange();
    range.selectNodeContents(el);

    if (!range.getClientRects) return;

    const initialLineCount = range.getClientRects().length;
    const { width: contentWidth } = range.getBoundingClientRect();

    const computedStyle = globalThis.getComputedStyle(el);
    const { width: totalWidth } = el.getBoundingClientRect();
    const horizontalPadding =
      Number.parseFloat(computedStyle.paddingLeft) +
      Number.parseFloat(computedStyle.paddingRight);

    const contentAreaWidth = totalWidth - horizontalPadding;
    const longestLineWidth = Math.ceil(contentWidth) + SUB_PIXEL_ROUNDING_GUARD;

    if (contentWidth > 0 && longestLineWidth < contentAreaWidth) {
      const isBorderBox = computedStyle.boxSizing === 'border-box';

      const width = isBorderBox
        ? longestLineWidth + horizontalPadding
        : longestLineWidth;

      el.style.width = `${width}px`;

      const newLineCount = range.getClientRects().length;

      if (newLineCount > initialLineCount) {
        el.style.width = '';
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useShrinkToFitWidth;
